import { countries, isCountryCode } from "@redotech/locale/countries";
import { useHandler } from "@redotech/react-util/hook";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  RcoNarySegmentEdge,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  DeliveryLocationCondition,
  DeliveryLocationNarySegment,
  DeliveryLocationRule,
  SegmentableCheckoutCharacteristic,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import React, { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";
import { FlowInProgress } from "../../../flow-editor";
import { deleteElementAndAllChildren } from "../../../utils/delete-element";
import { LocationRuleSelectionModal } from "./location-rule-selection-modal";

export const DeliveryLocationNaryInspectorContent = memo(
  function DeliveryLocationNaryInspectorContent({
    segment,
  }: {
    segment: DeliveryLocationNarySegment;
  }) {
    return (
      <Flex align="flex-start" dir="column" gap="lg">
        {segment.conditions.map((condition, index) => (
          <ConditionDisplay
            condition={condition}
            index={index}
            key={condition.edgeId}
          />
        ))}
      </Flex>
    );
  },
);

const ConditionDisplay = memo(function ConditionDisplay({
  condition,
  index,
}: {
  condition: DeliveryLocationCondition;
  index: number;
}) {
  return (
    <Flex align="flex-start" dir="column" gap="xs">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        Condition {index + 1}
      </Text>
      <Flex gap="xs" wrap="wrap">
        {condition.locationRules.map((rule) => {
          const countryName = isCountryCode(rule.countryCode)
            ? (countries.find((c) => c.code === rule.countryCode)?.name ??
              rule.countryCode)
            : rule.countryCode;
          const provinceCount = rule.provinces?.length ?? 0;
          const label =
            provinceCount > 0
              ? `${countryName} (${provinceCount} ${provinceCount === 1 ? "province" : "provinces"})`
              : countryName;
          return (
            <RedoBadge
              color="gray"
              key={rule.countryCode}
              size="xs"
              text={label}
            />
          );
        })}
      </Flex>
    </Flex>
  );
});

export const EditableDeliveryLocationNaryInspectorContent = memo(
  function EditableDeliveryLocationNaryInspectorContent({
    step,
    setStep,
    setAutomation,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
    setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
  }) {
    const segment = step.segment as DeliveryLocationNarySegment | undefined;
    const conditions = useMemo(
      () => segment?.conditions ?? [],
      [segment?.conditions],
    );

    const handleAddCondition = useHandler(() => {
      const newEdgeId = new ObjectId().toString();
      const newCondition: DeliveryLocationCondition = {
        edgeId: newEdgeId,
        locationRules: [],
      };
      const newEdge: RcoNarySegmentEdge = { edgeId: newEdgeId, nextId: "" };

      const updatedSegment: DeliveryLocationNarySegment = {
        segmentableCharacteristic:
          SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026,
        conditions: [...conditions, newCondition],
      };
      setStep({
        ...step,
        segment: updatedSegment,
        edges: [...(step.edges ?? []), newEdge],
      });
    });

    const handleRemoveCondition = useHandler((edgeId: string) => {
      const edgeToRemove = (step.edges ?? []).find((e) => e.edgeId === edgeId);
      const updatedConditions = conditions.filter((c) => c.edgeId !== edgeId);
      const updatedEdges = (step.edges ?? []).filter(
        (e) => e.edgeId !== edgeId,
      );
      const updatedSegment: DeliveryLocationNarySegment = {
        segmentableCharacteristic:
          SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026,
        conditions: updatedConditions,
      };
      setStep({ ...step, segment: updatedSegment, edges: updatedEdges });

      if (setAutomation && edgeToRemove?.nextId) {
        setAutomation((prev) => {
          if (!prev || !edgeToRemove.nextId) {
            return prev;
          }
          const updatedSteps = deleteElementAndAllChildren(
            edgeToRemove.nextId,
            prev.steps,
          );
          return { ...prev, steps: updatedSteps };
        });
      }
    });

    const handleUpdateConditionRules = useHandler(
      (edgeId: string, locationRules: DeliveryLocationRule[]) => {
        const updatedConditions = conditions.map((c) =>
          c.edgeId === edgeId ? { ...c, locationRules } : c,
        );
        const updatedSegment: DeliveryLocationNarySegment = {
          segmentableCharacteristic:
            SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026,
          conditions: updatedConditions,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    return (
      <Flex dir="column" gap="lg" w="full">
        {conditions.map((condition, index) => {
          const otherRules = conditions
            .filter((c) => c.edgeId !== condition.edgeId)
            .flatMap((c) => c.locationRules);
          return (
            <EditableCondition
              canDelete={conditions.length > 1}
              condition={condition}
              index={index}
              key={condition.edgeId}
              onClearRules={handleUpdateConditionRules}
              onRemove={handleRemoveCondition}
              onUpdateRules={handleUpdateConditionRules}
              otherConditionRules={otherRules}
            />
          );
        })}
        <RedoButton
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAddCondition}
          size="sm"
          text="Add condition"
        />
      </Flex>
    );
  },
);

const EditableCondition = memo(function EditableCondition({
  condition,
  index,
  canDelete,
  onRemove,
  onClearRules,
  onUpdateRules,
  otherConditionRules,
}: {
  condition: DeliveryLocationCondition;
  index: number;
  canDelete: boolean;
  onRemove: (edgeId: string) => void;
  onClearRules: (edgeId: string, rules: DeliveryLocationRule[]) => void;
  onUpdateRules: (edgeId: string, rules: DeliveryLocationRule[]) => void;
  otherConditionRules: DeliveryLocationRule[];
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleRulesSelected = useHandler((rules: DeliveryLocationRule[]) => {
    onUpdateRules(condition.edgeId, rules);
    setIsModalOpen(false);
  });

  const handleRemoveOrClear = useHandler(() => {
    if (canDelete) {
      onRemove(condition.edgeId);
    } else {
      onClearRules(condition.edgeId, []);
    }
  });

  return (
    <Flex
      align="flex-start"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="sm"
      p="md"
      radius="md"
      w="full"
    >
      <Flex justify="space-between" w="full">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Condition {index + 1}
        </Text>
        {(canDelete || condition.locationRules.length > 0) && (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={handleRemoveOrClear}
            size="xs"
          />
        )}
      </Flex>
      <Text fontSize="xs" fontWeight="medium" textColor="primary">
        Delivery location
      </Text>
      <Flex dir="row" gap="sm" wrap="wrap">
        {condition.locationRules.length > 0 ? (
          condition.locationRules.map((rule) => {
            const countryName = isCountryCode(rule.countryCode)
              ? (countries.find((c) => c.code === rule.countryCode)?.name ??
                rule.countryCode)
              : rule.countryCode;
            const provinceCount = rule.provinces?.length ?? 0;
            const label =
              provinceCount > 0
                ? `${countryName} (${provinceCount} ${provinceCount === 1 ? "province" : "provinces"})`
                : countryName;
            return (
              <RedoBadge
                color="gray"
                key={rule.countryCode}
                size="sm"
                text={label}
              />
            );
          })
        ) : (
          <Text fontSize="xs" textColor="tertiary">
            No locations selected
          </Text>
        )}
      </Flex>
      <Flex justify="flex-end" w="full">
        <RedoButton
          hierarchy="secondary"
          IconLeading={EditPencilIcon}
          onClick={() => setIsModalOpen(true)}
          size="xs"
          text="Edit locations"
        />
      </Flex>
      {isModalOpen && (
        <LocationRuleSelectionModal
          onClose={() => setIsModalOpen(false)}
          onRulesSelected={handleRulesSelected}
          otherConditionRules={otherConditionRules}
          selectedRules={condition.locationRules}
        />
      )}
    </Flex>
  );
});
